import * as Sentry from '@sentry/react';

import { DEFAULT_LOCALE, getStoredLocale, messages } from './index.js';

const LAZY_MARKER = '__LAZY_T__:';

/**
 * Translates a message key with optional placeholder values.
 * @param id - The translation key
 * @param values - Optional placeholder values
 * @returns The translated message
 */
export function translate(id: string, values?: Record<string, string | number>): string {
  const locale = getStoredLocale();
  let message = messages[locale][id] || messages[DEFAULT_LOCALE][id] || id;

  if (values) {
    Object.entries(values).forEach(([key, value]) => {
      message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value));
    });
  }

  return message;
}

/**
 * Creates a lazy translation marker for validation messages.
 * Instead of returning the translated string immediately (which would be "baked in"
 * at module load time), this returns a special marker string that gets resolved
 * at validation time by the zodResolver - allowing locale changes to take effect.
 *
 * @param id - The translation key (e.g., 'validation.login.usernameRequired')
 * @param values - Optional object with placeholder values (e.g., { field: 'Username', min: 3 })
 * @returns A marker string that will be translated at validation time
 */
export function t(id: string, values?: Record<string, string | number>): string {
  const payload = JSON.stringify({ id, values });
  return `${LAZY_MARKER}${payload}`;
}

/**
 * Resolves a lazy translation marker to its translated string.
 * If the message is not a lazy marker, returns it unchanged.
 */
export function resolveLazyTranslation(message: string): string {
  if (!message.startsWith(LAZY_MARKER)) {
    return message;
  }

  try {
    const payload = message.slice(LAZY_MARKER.length);
    const { id, values } = JSON.parse(payload) as {
      id: string;
      values?: Record<string, string | number>;
    };
    return translate(id, values);
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    return message;
  }
}
